import { MessageSquare } from 'lucide-react';
import { FormData } from '../../types';
import { personalitySliders } from '../../constants';
import { getPersonalityText } from '../../utils/helpers';
import { useRef, useState, useEffect } from 'react';

interface BrandPersonalityProps {
  formData: FormData;
  handleSliderChange: (sliderId: string, value: number) => void;
}

const containerStyle: React.CSSProperties = {
  padding: '24px',
  backgroundColor: '#FFFFFF',
  borderRadius: '16px',
};

const sliderContainerStyle: React.CSSProperties = {
  marginBottom: '48px',
};

const labelContainerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '24px',
};

const labelStyle: React.CSSProperties = {
  fontSize: '14px',
  fontWeight: 500,
  color: '#4B5563',
};

const sliderTrackStyle: React.CSSProperties = {
  position: 'relative',
  height: '24px',
  backgroundColor: '#F3F4F6',
  borderRadius: '12px',
  cursor: 'pointer',
};

const sliderProgressStyle: React.CSSProperties = {
  position: 'absolute',
  height: '100%',
  backgroundColor: 'rgba(99, 102, 241, 0.1)',
  borderRadius: '12px',
  transition: 'width 0.2s ease',
};

const dotsContainerStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '100%',
  display: 'flex',
  alignItems: 'center',
};

const dotBaseStyle: React.CSSProperties = {
  width: '16px',
  height: '16px',
  borderRadius: '50%',
  backgroundColor: '#FFFFFF',
  border: '2px solid #D1D5DB',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  position: 'absolute',
  transform: 'translateX(-50%)',
};

const activeDotStyle: React.CSSProperties = {
  backgroundColor: '#6366F1',
  border: 'none',
  boxShadow: '0 0 0 4px rgba(99, 102, 241, 0.2)',
};

const thumbStyle: React.CSSProperties = {
  position: 'absolute',
  width: '40px',
  height: '40px',
  backgroundColor: '#FFFFFF',
  borderRadius: '50%',
  border: '3px solid #6366F1',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  cursor: 'grab',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 10,
  transition: 'transform 0.2s ease',
};

const valueTextStyle: React.CSSProperties = {
  textAlign: 'center',
  fontSize: '14px',
  color: '#6B7280',
  marginTop: '12px',
};

// With sliders inline style

export const BrandPersonality = ({ formData, handleSliderChange }: BrandPersonalityProps) => {
  const stages = [0, 16.67, 33.33, 50, 66.67, 83.33, 100];
  const [isDragging, setIsDragging] = useState<string | null>(null);
  const [dragValue, setDragValue] = useState<{ [key: string]: number }>({});
  const sliderRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  useEffect(() => {
    const handleMove = (e: MouseEvent | TouchEvent) => {
      if (!isDragging || !sliderRefs.current[isDragging]) return;

      const slider = sliderRefs.current[isDragging];
      const rect = slider!.getBoundingClientRect();
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const value = x * 100;

      setDragValue(prev => ({ ...prev, [isDragging]: value }));
    };

    const handleEnd = () => {
      if (!isDragging) return;

      const value = dragValue[isDragging] || formData.personalitySliders[isDragging];
      const closestStage = stages.reduce((prev, curr) => 
        Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
      );

      handleSliderChange(isDragging, closestStage);
      setIsDragging(null);
      setDragValue({});
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleMove);
      document.addEventListener('touchend', handleEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, dragValue, formData.personalitySliders, handleSliderChange]);

  const handleStart = (sliderId: string, e: React.MouseEvent | React.TouchEvent) => {
    if (!sliderRefs.current[sliderId]) return;

    const slider = sliderRefs.current[sliderId];
    const rect = slider!.getBoundingClientRect();
    const clientX = 'touches' in e 
      ? e.touches[0].clientX 
      : (e as React.MouseEvent).clientX;
    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const value = x * 100;

    setDragValue({ [sliderId]: value });
    setIsDragging(sliderId);
  };

  return (
    <div style={containerStyle}>
      <h2 style={{ 
        fontSize: '24px',
        fontWeight: 600,
        color: '#1E3A8A',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <MessageSquare style={{ color: '#6366F1' }} />
        Brand Personality
      </h2>

      <div style={{ marginTop: '32px' }}>
        {personalitySliders.map(slider => (
          <div key={slider.id} style={sliderContainerStyle}>
            <div style={labelContainerStyle}>
              <span style={labelStyle}>{slider.left}</span>
              <span style={labelStyle}>{slider.right}</span>
            </div>

            <div style={{ position: 'relative', height: '24px' }}>
              <div
                ref={el => sliderRefs.current[slider.id] = el}
                style={sliderTrackStyle}
                onMouseDown={(e) => handleStart(slider.id, e)}
                onTouchStart={(e) => handleStart(slider.id, e)}
              >
                <div
                  style={{
                    ...sliderProgressStyle,
                    width: `${isDragging === slider.id 
                      ? dragValue[slider.id] 
                      : formData.personalitySliders[slider.id]}%`
                  }}
                />

                <div style={dotsContainerStyle}>
                  {stages.map((stage, index) => (
                    <div
                      key={stage}
                      onClick={() => handleSliderChange(slider.id, stage)}
                      style={{
                        ...dotBaseStyle,
                        ...(stage === formData.personalitySliders[slider.id] ? activeDotStyle : {}),
                        left: `${stage}%`,
                      }}
                    />
                  ))}
                </div>

                <div
                  style={{
                    ...thumbStyle,
                    left: `${isDragging === slider.id 
                      ? dragValue[slider.id] 
                      : formData.personalitySliders[slider.id]}%`,
                    transform: `translate(-50%, -50%) ${isDragging === slider.id ? 'scale(1.1)' : ''}`,
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setIsDragging(slider.id);
                    setDragValue({ [slider.id]: formData.personalitySliders[slider.id] });
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    setIsDragging(slider.id);
                    setDragValue({ [slider.id]: formData.personalitySliders[slider.id] });
                  }}
                />
              </div>
            </div>

            <div style={valueTextStyle}>
              {getPersonalityText(
                isDragging === slider.id 
                  ? dragValue[slider.id] 
                  : formData.personalitySliders[slider.id],
                slider.left,
                slider.right
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};